<template>
  <section>
    <h2 class="text-xl md:text-2xl font-bold mb-4 md:mb-6">小说封面生成</h2>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
      <div>
        <label class="block text-textSub mb-2">图片风格</label>
        <select v-model="style" class="w-full bg-white rounded-md p-3 focus:outline-none focus:ring-1 focus:ring-primary text-textSub border border-gray-200">
          <option value="">请选择风格</option>
          <option value="1">现代都市</option>
          <option value="2">古代历史</option>
          <option value="3">玄幻仙侠</option>
          <option value="4">科幻未来</option>
          <option value="5">言情甜宠</option>
        </select>
      </div>
    </div>

    <div class="mb-6">
      <label class="block text-textSub mb-2">故事描述（题材、核心场景、人物形象等）</label>
      <textarea v-model="desc" class="w-full bg-white rounded-md p-3 resize-y min-h-[200px] focus:outline-none focus:ring-1 focus:ring-primary border border-gray-200" placeholder=""></textarea>
      <p class="text-textSub text-sm mt-2">当前字符数：{{ desc.length }}/30000</p>
    </div>

    <button
      type="button"
      class="w-full bg-primary text-white py-3 rounded-md text-lg btn-hover mb-6 inline-flex items-center justify-center gap-2 flex-wrap"
      :disabled="creating"
      @click="startCreate"
    >
      <template v-if="creating">生成中...</template>
      <template v-else>
        <span>开始创作</span>
        <i class="fa-solid fa-bolt shrink-0 opacity-95" aria-hidden="true"></i>
        <span v-if="coinCost != null" class="text-sm font-normal opacity-90">{{ coinCost }}</span>
      </template>
    </button>

    <div v-show="resultVisible" class="bg-white rounded-md p-6 border border-gray-200">
      <h3 class="text-xl font-semibold mb-4">封面生成结果</h3>
      <div class="flex justify-center">
        <div class="w-64 h-96 bg-gray-100 rounded-md flex items-center justify-center border border-gray-200 overflow-hidden">
          <img v-if="coverImageUrl" :src="coverImageUrl" alt="封面预览" class="w-full h-full object-cover">
          <span v-else class="text-textSub">封面预览</span>
        </div>
      </div>
      <div class="mt-4 flex justify-center gap-4">
        <button class="bg-primary text-white px-4 py-2 rounded-md btn-hover" :disabled="!coverImageUrl" @click="downloadCover">下载封面</button>
      </div>
      <p class="mt-3 text-center text-sm text-textSub">
        提示：生成的封面图片请尽快下载，平台不会保留历史数据。
      </p>
    </div>

    <!-- 生成中进度弹层 -->
    <div
      v-if="creating"
      class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-lg px-6 py-5 shadow-md border border-gray-200 max-w-sm w-full mx-4">
        <div class="flex items-center justify-center gap-2 mb-4">
          <i class="fa-solid fa-spinner fa-spin text-primary"></i>
          <div class="text-primary text-lg font-semibold">封面生成中...</div>
        </div>
        <div class="space-y-2 text-sm">
          <div class="flex items-start gap-2 text-textMain">
            <div class="mt-0.5">
              <i
                v-if="getStepStatus(0) === 'FAILED'"
                class="fa-solid fa-circle-xmark text-red-500"
              ></i>
              <i
                v-else-if="getStepStatus(0) === 'SUCCESS'"
                class="fa-solid fa-circle-check text-green-500"
              ></i>
              <i
                v-else
                class="fa-solid fa-circle text-gray-300"
              ></i>
            </div>
            <div class="flex-1">
              <div class="grid grid-cols-[1fr_12rem] items-center gap-3 leading-6">
                <span class="font-medium">封面绘制中</span>
                <span class="text-textSub whitespace-nowrap text-right">预计1分钟，请耐心等待</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="guide-module">
      <h3 class="guide-title">
        <i class="fa-solid fa-lightbulb guide-icon"></i>
        封面生成使用攻略
      </h3>
      <div class="guide-list">
        <div class="guide-item"><i class="fa-solid fa-check-circle guide-icon"></i><span>故事描述需突出核心场景和人物特征，封面更贴合</span></div>
        <div class="guide-item"><i class="fa-solid fa-check-circle guide-icon"></i><span>选择风格时结合小说类型，古风小说选古风风格</span></div>
        <div class="guide-item"><i class="fa-solid fa-check-circle guide-icon"></i><span>小说名称建议简洁易记，避免过长影响封面排版</span></div>
      </div>
    </div>
  </section>
</template>

<script>
import { createCover, queryWorkTask } from '../api/cover'
import coinCostActive from '../mixins/coinCostActive'
import { poll } from '../utils/poll'

export default {
  name: 'CoverSection',
  coinCostKey: 'cover',
  mixins: [coinCostActive],
  data () {
    return {
      style: '',
      desc: `竖版网文小说封面
书名：XXX
作者：XXX
风格：XXX
字体：XXX
云海仙山，御剑白衣仙君，水墨丹青质感，朱砂鎏金配色，8K高清，电影级光影，构图大气磅礴，书名鎏金书法大字居中，作者名雅致小字下方居中排版，画面干净高级，无水印无杂物，古风字体融合自然。`,
      creating: false,
      creatingLastResult: null,
      taskId: '',
      resultVisible: false,
      coverImageUrl: ''
    }
  },
  methods: {
    getStepStatus (idx) {
      const res = this.creatingLastResult
      if (!res) return ''
      const n = idx + 1
      const statusKey = 'step_' + n + '_status'
      const direct = res && res[statusKey]
      if (direct === 'SUCCESS' || direct === 'FAILED') return direct
      const progress = res && res.taskProgress
      if (typeof progress === 'string' && progress.indexOf('=') > -1) {
        const parts = progress.split('=').map(s => s.trim())
        if (parts[0] === String(n) && (parts[1] === 'SUCCESS' || parts[1] === 'FAILED')) return parts[1]
      }
      return ''
    },
    pollTaskResult (taskId) {
      return poll(() => queryWorkTask(taskId), {
        onResult: (res) => {
          this.creatingLastResult = res || null
        },
        shouldStop: function (res) {
          return res && res.taskStatus === 'SUCCESS'
        },
        isError: function (res) {
          if (res && res.taskStatus === 'FAILED') {
            const msg = (res && (res.message || res.msg)) || '任务执行失败,不扣除点数'
            return new Error(msg)
          }
          return null
        },
        interval: 15000,
        maxTimes: 150
      })
    },
    async startCreate () {
      if (this.creating) return
      const style = (this.style || '').trim()
      const desc = (this.desc || '').trim()
      if (!style) {
        this.$emit('show-modal', '提示', '请选择图片风格。')
        return
      }
      if (!desc) {
        this.$emit('show-modal', '提示', '请输入故事描述。')
        return
      }
      this.creating = true
      this.creatingLastResult = null
      this.taskId = ''
      this.resultVisible = false
      this.coverImageUrl = ''
      try {
        const res = await createCover({
          style: style,
          desc: desc
        })
        const taskId = (res && (res.taskId != null ? res.taskId : (res.data && res.data.taskId))) || ''
        if (taskId) {
          this.taskId = String(taskId)
          const data = await this.pollTaskResult(taskId)
          const imageUrlFromTask = (data && data.coverImgUrl) ||
            (data && data.data && data.data.coverImgUrl) ||
            (data && (data.imageUrl || data.url || data.coverUrl || data.path)) ||
            (data && data.data && (data.data.imageUrl || data.data.url || data.data.coverUrl || data.data.path)) || ''
          if (!imageUrlFromTask) {
            this.$emit('show-modal', '生成失败', '任务完成但未返回封面地址，请稍后重试。')
            return
          }
          this.coverImageUrl = String(imageUrlFromTask)
          this.resultVisible = true
          return
        }

        // 兼容后端直接返回封面地址（无 taskId）的场景
        const imageUrl = (res && res.coverImgUrl) ||
          (res && res.data && res.data.coverImgUrl) ||
          (res && (res.imageUrl || res.url || res.coverUrl || res.path)) ||
          (res && res.data && (res.data.imageUrl || res.data.url || res.data.coverUrl || res.data.path)) || ''
        if (!imageUrl) {
          this.$emit('show-modal', '生成失败', '接口未返回任务ID或封面地址，请稍后重试。')
          return
        }
        this.coverImageUrl = String(imageUrl)
        this.resultVisible = true
      } catch (err) {
        this.$emit('show-modal', '生成失败', (err && err.message) || '封面生成失败，请稍后重试。')
      } finally {
        this.creating = false
      }
    },
    downloadCover () {
      if (!this.coverImageUrl) return
      window.open(this.coverImageUrl, '_blank')
    }
  }
}
</script>
